import React from 'react';
import { curveBasis as d3CurveBasis, line as d3Line } from 'd3-shape';
import { token } from '@atlaskit/tokens';
import colors from '@atlassian/jira-portfolio-3-common/src/colors/index.tsx';
import ConnectionComponent from './connection/index.tsx';
import type { Props, Rectangle, Point } from './types.tsx';

/** Create a Rectangle from a pair of its opposite vertices */
export function rectangleFromDiagonal(start: Point, end: Point): Rectangle {
	return {
		x: Math.min(start.x, end.x),
		y: Math.min(start.y, end.y),
		w: Math.abs(end.x - start.x),
		h: Math.abs(end.y - start.y),
	};
}

export default function Connections({
	connections,
	size,
	onLinkClick,
	onDeleteLink,
	currentDetailFlyoutId,
	Connection = ConnectionComponent,
}: Props) {
	const paths = connections.map(
		({ edge: { points }, link: { label, isRollup, links, highlight, isNegativeLeadTime }, id }) => {
			const line = d3Line()
				.x((d) => d[0])
				.y((d) => d[1])
				.curve(d3CurveBasis)(points.map((p) => [p.x, p.y]));

			const { x, y, w, h } = rectangleFromDiagonal(points[0], points[2]);

			return (
				<Connection
					key={id}
					id={id}
					line={line}
					x={x}
					y={y}
					h={h}
					w={w}
					label={label}
					isRollup={isRollup}
					links={links}
					onLinkClick={onLinkClick}
					onDeleteLink={onDeleteLink}
					highlight={highlight}
					isDetailOpen={currentDetailFlyoutId === id}
					isNegativeLeadTime={isNegativeLeadTime}
				/>
			);
		},
	);

	return (
		<svg
			style={{
				height: size.height,
				width: size.width,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				minHeight: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				minWidth: '100%',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				position: 'absolute',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				zIndex: 0,
			}}
		>
			<defs>
				<marker id="markerArrow" markerWidth="33" markerHeight="33" refX="7" refY="6" orient="auto">
					<path
						d="M2,2 L2,11 L10,6 L2,2"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						style={{ fill: token('color.background.neutral', colors.N90) }}
					/>
				</marker>
				<marker id="markerStart" markerWidth="33" markerHeight="33" refX="2" refY="6" orient="auto">
					<path
						d="M10,2 L10,11 L2,6 L10,2"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
						style={{ fill: token('color.background.neutral', colors.N90) }}
					/>
				</marker>
			</defs>
			<g>{paths}</g>
		</svg>
	);
}
